// The conversation's derived state, minus React — the pure half of ChannelViewModel: rows in
// reading order with grouping/day/unread separators, reply footers, reaction chips, seen checks,
// resolved authors. Built from ONE worker read (`ChannelPage`).
import type { ChannelPage } from '../../bridge/protocol';
import { userDisplayName } from '../../core/channelListDerivation';
import { MessageListDerivation, type MessageListRow } from '../../core/messageListDerivation';
import { MessageAttachments } from '../../core/messageAttachments';
import { ReactionsDerivation, type ReactionChip } from '../../core/reactionsDerivation';
import { PostState, type PostRecord, type UserRecord } from '../../core/records';
import { ThreadFooterDerivation, type ThreadFooter } from '../../core/threadFooterDerivation';
import { ReactionTime } from '../../core/quickReactions';
import { t } from '../../i18n/i18n';

export type SeenLevel = 'sending' | 'sent' | 'some' | 'all' | null;

export interface ConversationModel {
  rows: MessageListRow[];
  footers: Map<string, ThreadFooter>;
  chips: Map<string, ReactionChip[]>;
  usersById: Map<string, UserRecord>;
  seen: Map<string, SeenLevel>;
  knownUsernames: Set<string>;
  authorName(post: PostRecord): string;
  /** Ids the page names but the store lacks — the view asks the worker to fetch them. */
  unknownAuthorIds: string[];
  failedCount: number;
  pendingCount: number;
}

/** One scale, three steps, identical on all clients (docs/PARITY.md "Send-state"). */
export function seenLevel(post: PostRecord, me: string, summary: { anySeen: boolean; allSeen: boolean } | undefined): SeenLevel {
  if (post.userId !== me || post.type.startsWith('system_')) return null;
  // A clock while the send is on its way; the message itself is drawn at full strength (2026-10-16).
  if (post.state === PostState.Pending) return 'sending';
  if (post.state !== PostState.Synced) return null;
  if (!summary) return 'sent';
  if (summary.allSeen) return 'all';
  if (summary.anySeen) return 'some';
  return 'sent';
}

export function buildConversation(
  page: ChannelPage,
  me: string,
  unreadBoundary: number | null,
  now: number,
  echoes: PostRecord[] = [],
  isThread = false,
): ConversationModel {
  // Reading order (oldest first). The page arrives newest-first; derive chronologically, never on
  // the reversed list (the Android "reversing posts first breaks the derivation" trap).
  const byId = new Map<string, PostRecord>();
  const source = isThread ? page.posts : [...page.posts].reverse();
  for (const p of source) byId.set(p.id, p);
  // An echo is the message on screen BEFORE the store write lands; it yields to the stored row.
  for (const e of echoes) if (!byId.has(e.id) && !page.posts.some((p) => p.pendingId === e.id)) byId.set(e.id, e);
  for (const e of page.ephemeral) byId.set(e.id, e);
  const posts = [...byId.values()]
    .filter((p) => p.deleteAt === 0)
    .sort((a, b) => (isThread && a.id === page.posts[0]?.id ? -1 : isThread && b.id === page.posts[0]?.id ? 1 : a.createAt - b.createAt || (a.id < b.id ? -1 : 1)));

  const rows = MessageListDerivation.rows(posts, unreadBoundary, { now });
  const usersById = new Map(page.users.map((u) => [u.id, u]));
  const summaries = new Map(Object.entries(page.threadSummaries));
  const footers = isThread ? new Map() : ThreadFooterDerivation.footers(posts, summaries);
  const reactions = new Map(Object.entries(page.reactions));
  const chips = ReactionsDerivation.chipsByPostWithTooltips(reactions, {
    currentUserId: me,
    usersById,
    youLabel: t('channel.reaction.you'),
    unknownLabel: t('channel.reaction.unknown'),
    // Who AND when on hover (the Mac's chip `.help`): «Ana Torres · 10:42» per line.
    timeLabel: (ms) => ReactionTime.label(ms, { now }),
  });
  const seen = new Map<string, SeenLevel>();
  for (const p of posts) seen.set(p.id, seenLevel(p, me, page.seen[p.id]));
  const knownUsernames = new Set(page.users.map((u) => u.username).filter(Boolean));
  // Reactors too (ChannelViewModel.swift's missingReactorIds): the chip tooltip must name them,
  // and a reactor is often nobody who wrote on this page.
  const unknownAuthorIds = [
    ...new Set([...posts.map((p) => p.userId), ...ReactionsDerivation.reactorIds(reactions)].filter((id) => id && !usersById.has(id))),
  ];
  let failedCount = 0;
  let pendingCount = 0;
  for (const p of posts) {
    if (p.state === PostState.Failed) failedCount++;
    else if (p.state === PostState.Pending) pendingCount++;
  }
  const unknown = t('mac.unknownAuthor');
  return {
    rows,
    footers,
    chips,
    usersById,
    seen,
    knownUsernames,
    unknownAuthorIds,
    failedCount,
    pendingCount,
    // An integration's declared identity beats the account it travelled through.
    authorName: (post) => {
      const hook = MessageAttachments.webhookUsername(post.props);
      if (hook) return hook;
      const u = usersById.get(post.userId);
      return u ? userDisplayName(u) : unknown;
    },
  };
}
